import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class TabNavigationService {
    private activeTabSubject = new BehaviorSubject<'wells' | 'requests'>('wells');
    public activeTab$ = this.activeTabSubject.asObservable();
    
    private uploadModalSubject = new BehaviorSubject<boolean>(false);
    public uploadModal$ = this.uploadModalSubject.asObservable();
    
    private uploadTypeModalSubject = new BehaviorSubject<boolean>(false);
    public uploadTypeModal$ = this.uploadTypeModalSubject.asObservable();
    
    private selectedUploadTypeSubject = new BehaviorSubject<'excel' | 'raw' | null>(null);
    public selectedUploadType$ = this.selectedUploadTypeSubject.asObservable();
    
    private resetWellsSubject = new BehaviorSubject<boolean>(false);
    public resetWells$ = this.resetWellsSubject.asObservable();
    
    private isInWellViewSubject = new BehaviorSubject<boolean>(false);
    public isInWellView$ = this.isInWellViewSubject.asObservable();
    
    private selectedSchemaSubject = new BehaviorSubject<string>('');
    public selectedSchema$ = this.selectedSchemaSubject.asObservable();
    
    private selectedSchemaApiNameSubject = new BehaviorSubject<string>('');
    public selectedSchemaApiName$ = this.selectedSchemaApiNameSubject.asObservable();
    
    private stagesCompletedSubject = new BehaviorSubject<string[]>([]);
    public stagesCompleted$ = this.stagesCompletedSubject.asObservable();
    
    // Flag to indicate we're in a valid navigation flow (e.g., coming back from review)
    // This prevents refresh detection from blocking legitimate navigation
    private validNavigationFlowSubject = new BehaviorSubject<boolean>(false);
    public validNavigationFlow$ = this.validNavigationFlowSubject.asObservable();

    public setActiveTab(tab: 'wells' | 'requests'): void {
        this.activeTabSubject.next(tab);
    }

    public getActiveTab(): 'wells' | 'requests' {
        return this.activeTabSubject.value;
    }
    
    public openUploadModal(): void {
        this.uploadModalSubject.next(true);
    }
    
    public closeUploadModal(): void {
        this.uploadModalSubject.next(false);
    }
    
    public openUploadTypeModal(): void {
        this.uploadTypeModalSubject.next(true);
    }
    
    public closeUploadTypeModal(): void {
        this.uploadTypeModalSubject.next(false);
    }
    
    public setSelectedUploadType(type: 'excel' | 'raw'): void {
        this.selectedUploadTypeSubject.next(type);
    }
    
    public getSelectedUploadType(): 'excel' | 'raw' | null {
        return this.selectedUploadTypeSubject.value;
    }
    
    public resetSelectedUploadType(): void {
        this.selectedUploadTypeSubject.next(null);
    }
    
    public triggerResetWells(): void {
        this.resetWellsSubject.next(true);
        // Reset after a short delay
        setTimeout(() => {
            this.resetWellsSubject.next(false);
        }, 100);
    }
    
    public setInWellView(isInView: boolean): void {
        this.isInWellViewSubject.next(isInView);
    }
    
    public getIsInWellView(): boolean {
        return this.isInWellViewSubject.value;
    }
    
    public setSelectedSchema(displayName: string, apiName: string): void {
        this.selectedSchemaSubject.next(displayName);
        this.selectedSchemaApiNameSubject.next(apiName);
    }
    
    public getSelectedSchema(): string {
        return this.selectedSchemaSubject.value;
    }
    
    public getSelectedSchemaApiName(): string {
        return this.selectedSchemaApiNameSubject.value;
    }
    
    public clearSelectedSchema(): void {
        this.selectedSchemaSubject.next('');
        this.selectedSchemaApiNameSubject.next('');
    }
    
    public setStagesCompleted(stages: string[]): void {
        // Ensure stages is always an array
        const validStages = Array.isArray(stages) ? stages : [];
        this.stagesCompletedSubject.next(validStages);
    }
    
    public getStagesCompleted(): string[] {
        const stages = this.stagesCompletedSubject.value;
        // Ensure return value is always an array
        return Array.isArray(stages) ? stages : [];
    }
    
    public clearStagesCompleted(): void {
        this.stagesCompletedSubject.next([]);
    }
    
    /**
     * Mark that we're in a valid navigation flow (e.g., returning from review)
     * This prevents refresh detection from blocking legitimate navigation
     */
    public setValidNavigationFlow(isValid: boolean): void {
        this.validNavigationFlowSubject.next(isValid);
        // Auto-clear after a short delay (300ms) to avoid interfering with subsequent navigations
        if (isValid) {
            setTimeout(() => {
                this.validNavigationFlowSubject.next(false);
            }, 300);
        }
    }
    
    public isValidNavigationFlow(): boolean {
        return this.validNavigationFlowSubject.value;
    }
}
